/**
 * The classes an import run did not create, as an enrollment CSV.
 *
 * The import screen's failure list lives only in page state, so navigating away lost it — and in
 * a two-hundred-class block, working out afterwards which classes are missing is slow. This
 * writes them out in the **same format the enrollment upload reads**, so the file is both the
 * to-do list and, once the problems are fixed, the thing to upload again.
 *
 * The eighth column says why each class was left out. The upload accepts seven or eight columns
 * and ignores the eighth, so the reason rides along without breaking a re-upload.
 */

export interface CsvSession {
    day: string;
    startTime: string; // HH:MM
    endTime: string;   // HH:MM
}

export interface CsvClass {
    id: string;
    sku: string;
    name: string;
    teachers: { id: number; identifier?: string }[];
    students: { identifier: string }[];
    sessions: CsvSession[];
}

export const ENROLLMENT_HEADER = [
    'Student Identifier', 'Teacher Identifier', 'SKU', 'Common Name', 'DayTime',
    'Number of Sessions', 'Duration of Sessions (minutes)', 'Problem',
];

/** The upload splits on bare commas and newlines, so neither may appear inside a field. */
const field = (value: unknown): string => String(value ?? '').replace(/[,\r\n]+/g, ' ').trim();

const minutes = (hhmm: string): number => {
    const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
    return h * 60 + m;
};

/**
 * `Mon_13.00+Fri_12.00` — the distinct weekly slots, in the order they first occur. The upload
 * regenerates the dates from these and the block start, so this round-trips the schedule as
 * long as the block start date has not been changed in between.
 */
export const dayTimeFor = (sessions: CsvSession[]): string => {
    const seen: string[] = [];
    for (const s of sessions) {
        const slot = `${s.day}_${s.startTime.replace(':', '.')}`;
        if (!seen.includes(slot)) seen.push(slot);
    }
    return seen.join('+');
};

/**
 * One row per student, as the upload expects. Teachers go on those rows in turn; if there are
 * more teachers than students the first student is repeated for them (the upload de-duplicates
 * students within a class). A class with nobody on it still gets one row, so it is not lost.
 */
export const rowsForClass = (
    cls: CsvClass,
    problem: string,
    teacherIdentifier: (teacherId: number) => string | undefined,
): string[][] => {
    const teacherIds = cls.teachers.map((t) => t.identifier || teacherIdentifier(t.id) || '');
    const studentIds = cls.students.map((s) => s.identifier);
    const count = Math.max(1, studentIds.length, teacherIds.length);

    const first = cls.sessions[0];
    const duration = first ? minutes(first.endTime) - minutes(first.startTime) : '';

    const rows: string[][] = [];
    for (let i = 0; i < count; i++) {
        rows.push([
            studentIds[i] ?? studentIds[0] ?? '',
            teacherIds.length > 0 ? teacherIds[i % teacherIds.length] : '',
            cls.sku,
            cls.name,
            dayTimeFor(cls.sessions),
            String(cls.sessions.length),
            String(duration),
            problem,
        ].map(field));
    }
    return rows;
};

export const buildNotImportedCsv = (
    classes: Array<{ cls: CsvClass; problem: string }>,
    teacherIdentifier: (teacherId: number) => string | undefined,
): string =>
    [ENROLLMENT_HEADER, ...classes.flatMap(({ cls, problem }) => rowsForClass(cls, problem, teacherIdentifier))]
        .map((row) => row.join(','))
        .join('\n') + '\n';
